import { useEffect, useState } from "react";
import * as Schema from "effect/Schema";

import { useLocalStorage } from "./useLocalStorage";
import {
  getThreadNotificationPermission,
  threadNotificationsPreferenceKey,
} from "../threadCompletionNotifications";

const preferenceSchema = Schema.NullOr(Schema.Boolean);

export function useThreadNotifications(email: string) {
  const [preference, setPreference] = useLocalStorage(
    threadNotificationsPreferenceKey(email),
    null,
    preferenceSchema,
  );
  const [permission, setPermission] = useState(getThreadNotificationPermission);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const refresh = () => setPermission(getThreadNotificationPermission());
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, []);

  const enable = async () => {
    setError(null);
    setRequesting(true);
    try {
      // Called directly from the user's click to preserve browser user activation.
      const result = await Notification.requestPermission();
      setPermission(result);
      setPreference(result === "granted");
    } catch {
      setError("Could not enable notifications. Check your browser's site settings and try again.");
    } finally {
      setRequesting(false);
    }
  };

  return {
    active: preference !== false && permission === "granted",
    shouldPrompt: preference === null && permission === "default",
    permission,
    requesting,
    error,
    enable,
    disable: () => {
      setError(null);
      setPreference(false);
    },
  };
}
